// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { NodeFeed } from './conductors/flowPulse';
import type { StatusToken } from '../../../utils/entityHelpers';
import type { PowerFlowNode } from '../../../utils/powerFlowLayout';
import type { AssetBadge, MeterReading } from '../../../utils/powerFlowCaptions';
import { DownstreamMark, type DownstreamStatus } from './primitives/DownstreamMark';
import { StatusMark } from './primitives/StatusMark';
import { withAlarmLines } from '../../../utils/powerFlowAlarms';

export interface SldBusProps {
  node: PowerFlowNode;
  status: StatusToken;
  /** Whether power is reaching it: the status dot pulses with the flow, or dims. */
  feed?: NodeFeed;
  label: string;
  accessibleName: string;
  /** The meter drawn on this bus: volts, power, current. */
  meter?: MeterReading;
  /** What the bus feeds, e.g. `Critical load`. Drawn under the bar. */
  sublabel?: string;
  /** The N+1 verdict, or a both-sources warning. */
  badge?: AssetBadge;
  /** The worst status among loads folded under this bar and not drawn. */
  downstream?: DownstreamStatus;
  stale?: boolean;
  selected: boolean;
  highlighted: boolean;
  /** Exactly one asset per rank is focusable at a time (roving tabIndex). */
  focusable: boolean;
  onSelect: (_id: string) => void;
  /** One line per open alarm on this bus. Reaches the hover title only. */
  alarmLines?: string[];
}

/**
 * A bus, drawn as a bar with its meter beside it.
 *
 * **Every bus is a bar now, at every depth.** A switchboard, a panelboard and
 * an RPP are the same electrical idea at different ratings: everything tapped
 * off them sits at one potential, which is the most useful single thing a
 * one-line says. Drawing the shallowest one as a bar and the rest as boxes said
 * they were different kinds of object. The bar spans the horizontal extent of
 * what it feeds, with short vertical taps down to each child, and
 * `powerFlowLayout` keeps each rank contiguous by block so a bar can never span
 * a neighbouring block's assets.
 *
 * **The meter belongs on the bus, not inside a box.** A one-line puts the panel
 * meter on the run it measures and prints volts, power and current beside it —
 * `PQM1A · 480 V · 8.5 MW · 3,898 A`. We have no meter entities, so the block
 * is the board's own readings presented where an engineer looks for them.
 *
 * The label sits above the bar because the bar is 10px tall. The hit target is
 * the label strip plus the bar, not the 10px strip: a bar-only target would be
 * unusable with a mouse and impossible on a touch screen.
 */
const SldBusComponent = ({
  node,
  status,
  feed,
  label,
  accessibleName,
  meter,
  sublabel,
  badge,
  downstream,
  stale,
  selected,
  highlighted,
  focusable,
  onSelect,
  alarmLines,
}: SldBusProps) => {
  // Two weights, not five. `switchgear` is the heavy bar; busway, panelboard,
  // MCC and RPP all collapse to the light one -- they are the same electrical
  // idea at a smaller rating, and four distinct bar styles would be noise.
  const weight = node.vertex.symbol === 'switchgear' ? 'heavy' : 'light';
  const classes = ['sld-bus', `sld-bus--${status}`, `sld-bus--${weight}`];
  if (selected) classes.push('is-selected');
  if (highlighted) classes.push('is-highlighted');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onSelect(node.id);
    }
  };

  const LABEL_HEIGHT = 20;

  return (
    <div
      className={classes.join(' ')}
      // The wrapper covers the label strip and the bar, so the whole thing is
      // the hit target; the bar itself is the child element below.
      style={{
        left: node.x,
        top: node.y - LABEL_HEIGHT,
        width: node.w,
        height: node.h + LABEL_HEIGHT,
      }}
      role="gridcell"
      tabIndex={focusable ? 0 : -1}
      aria-label={accessibleName}
      title={withAlarmLines(undefined, alarmLines)}
      aria-current={selected ? 'true' : undefined}
      data-sld-node={node.id}
      data-sld-status={status}
      data-sld-symbol={node.vertex.symbol}
      data-sld-rank={node.rank}
      onClick={() => onSelect(node.id)}
      onKeyDown={handleKeyDown}
    >
      <span className="sld-bus__label">
        <StatusMark status={status} feed={feed} />
        <DownstreamMark downstream={downstream} className="sld-bus__downstream" />
        <span className="sld-bus__name">{label}</span>
        {meter && (
          <span className={`sld-meter${stale ? ' is-stale' : ''}`} data-sld-meter>
            {meter.volts && <span className="sld-meter__value">{meter.volts}</span>}
            {meter.power && <span className="sld-meter__value">{meter.power}</span>}
            {meter.current && <span className="sld-meter__value">{meter.current}</span>}
          </span>
        )}
        {badge && (
          <span
            className={`sld-badge sld-badge--${badge.tone}`}
            data-sld-badge={badge.tone}
            title={badge.rule}
          >
            {badge.text}
          </span>
        )}
      </span>
      <span className="sld-bus__bar" style={{ height: node.h }} aria-hidden="true" />
      {sublabel && (
        <span className="sld-bus__sublabel" data-sld-sublabel>
          {sublabel}
        </span>
      )}
    </div>
  );
};

export const SldBus = React.memo(SldBusComponent);
